<script lang="ts" setup>
import { onUnmounted, ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { cloneDeep } from 'lodash';

import { AppLinkButton } from '@libs/ui';
import type { Pax } from '@libs/trip-planner-interfaces';

import { PaxSelectorEventType } from './PaxSelector.model';
import PaxInRoom from './paxInRoom/PaxInRoom.vue';
import type { PaxSelectorEvents, RoomPaxConfig } from './PaxSelector.model';
import PaxSelectorMessages from './PaxSelectorMessages.vue';
import { usePaxSelectorStore } from './stores/paxSelectorStore';

interface PaxSelectorViewProps {
  initialPaxes: RoomPaxConfig[];
  shouldShowTicketsWarning: boolean;
  shouldShowRoomSelectionWarning: boolean;
  shouldShowNumberOfVehiclesUpdatedWarning: boolean;
  shouldShowNumberOfVehiclesWillBeUpdatedWarning: boolean;
  emitDebounceTimeMs: number;
}

const props = defineProps<PaxSelectorViewProps>();

interface PaxSelectorViewEmits {
  (event: 'update-pax', newPax: Pax): void;
}

const emit = defineEmits<PaxSelectorViewEmits>();

const paxSelectorStore = usePaxSelectorStore();

// NOTE:
// We want to display message box only after user made at least one change
// in paxes. This behaviour have to reset each time modal is closed, hence
// having piece of state in component gives us this behaviour for free
// due to component lifecycle.
const hasUserMadeAction = ref(false);

paxSelectorStore.initializeState(props.initialPaxes);

function handleEvent(action: PaxSelectorEvents): void {
  hasUserMadeAction.value = true;
  switch (action.eventType) {
    case PaxSelectorEventType.AdultIncrease:
      paxSelectorStore.increaseAmountOfAdultsInRoom(action.roomIndex);
      break;
    case PaxSelectorEventType.AdultDecrease:
      paxSelectorStore.decreaseAmountOfAdultsInRoom(action.roomIndex);
      break;
    case PaxSelectorEventType.ChildIncrease:
      paxSelectorStore.increaseAmountOfChildrenInRoom(action.roomIndex);
      break;
    case PaxSelectorEventType.ChildDecrease:
      paxSelectorStore.decreaseAmountOfChildrenInRoom(action.roomIndex);
      break;
    case PaxSelectorEventType.UpdateChildAge:
      paxSelectorStore.updateChildAgeInRoom(
        action.roomIndex,
        action.childIndex,
        action.age
      );
      break;
    case PaxSelectorEventType.AddRoom:
      paxSelectorStore.addRoom();
      break;
    case PaxSelectorEventType.RemoveRoom:
      paxSelectorStore.removeRoom(action.roomIndex);
      break;
  }
  emitWithDebounce();
}

const emitWithDebounce = useDebounceFn(() => {
  const asPax = paxSelectorStore.asPax;
  // NOTE: Without clone deep debounce won't work, because store internally mutates object.
  emit('update-pax', cloneDeep(asPax));
}, props.emitDebounceTimeMs);

onUnmounted(() => {
  // NOTE: When component is used with non zero debounce time we have to emit update pax event on unmounted.
  // otherwise update won't be emitted, if user closes panel / modal before debounce fires.
  if (props.emitDebounceTimeMs > 0) {
    const asPax = paxSelectorStore.asPax;
    emit('update-pax', cloneDeep(asPax));
  }
});
</script>

<template>
  <PaxSelectorMessages
    :has-user-made-action="hasUserMadeAction"
    :should-show-tickets-warning="props.shouldShowTicketsWarning"
    :should-show-room-selection-warning="props.shouldShowRoomSelectionWarning"
    :should-show-number-of-vehicles-updated-warning="
      props.shouldShowNumberOfVehiclesUpdatedWarning
    "
    :should-show-number-of-vehicles-will-be-updated-warning="
      props.shouldShowNumberOfVehiclesWillBeUpdatedWarning
    "
  />
  <div class="pax-selector">
    <PaxInRoom
      v-for="(room, index) in paxSelectorStore.roomsPaxConfig"
      :key="room.uniqueId"
      class="pax-in-room"
      :room-index="index"
      :room-pax-config="room"
      @pax-event="handleEvent"
    />
    <AppLinkButton
      :text="$t('planner.pax.add_room')"
      size="m"
      class="add-room-button"
      @click="handleEvent({ eventType: PaxSelectorEventType.AddRoom })"
    />
  </div>
</template>

<style lang="scss" scoped>
.pax-selector {
  width: 100%;

  .pax-in-room {
    margin-bottom: var(--spacing-16);
  }

  .add-room-button {
    margin-bottom: var(--spacing-24);
  }
}
</style>
